import { useMemo, useRef, useState } from 'react';
import { Icon } from '../../../layout/Icon.jsx';
import { Pill } from '../../../layout/Pill.jsx';
import { useAuth } from '../../../shared/useAuth.js';
import { formatIQD, formatNumber, formatDateTime } from '../../../shared/format.js';
import { PrintLogo } from '../../../shared/PrintLogo.jsx';
import { usePurchasesTopProducts, useCancelledPurchases } from '../hooks/useReports.js';
import { ExportButton } from './ExportButton.jsx';
import './print.css';

// قسم «المشتريات» في نافذة التقارير — نظير قسم المبيعات تماماً: نفس
// التصميم (بطاقات + جدول + شريط حصة)، ونفس السلوك (ترشيح بالمدى والمورد
// والمخزن، فرز، طباعة A4، تصدير PDF/Excel/CSV)، لكن على فواتير الشراء.
//
// القواعد المحاسبية محفوظة: المسوّدات لا تُحتسب، والفواتير الملغاة مستثناة
// من «أكثر المنتجات شراءً» وتُعرض في تقريرها الخاص بلا حذف.

const pad = (n) => String(n).padStart(2, '0');

function dateAr(v) {
  if (!v) return '—';
  const d = v instanceof Date ? v : new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
}

function periodLabelOf(range) {
  const f = String(range?.from || '').slice(0, 10);
  const t = String(range?.to || '').slice(0, 10);
  if (!f && !t) return '';
  return f === t ? f : `${f} ← ${t}`;
}

function KpiCard({ label, value, sub, accent }) {
  return (
    <div
      className="kpi-card"
      style={{
        border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', background: '#fff'
      }}
    >
      <div style={{ fontSize: 11.5, color: '#6b7280', fontWeight: 600 }}>{label}</div>
      <div
        style={{
          fontSize: 18,
          fontWeight: 800,
          marginTop: 4,
          color: accent || '#111827',
          letterSpacing: '-.02em'
        }}
      >
        {value}
      </div>
      {sub ? <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 2 }}>{sub}</div> : null}
    </div>
  );
}

/** تفاصيل أصناف فاتورة ملغاة — تُفتح داخل الصفحة لا في نافذة منبثقة. */
function CancelledInvoiceDetails({ row, onClose }) {
  return (
    <tr>
      <td colSpan={8} style={{ background: '#FFF7ED', padding: 0 }}>
        <div style={{ padding: '10px 14px' }}>
          <div className="flex items-center gap-2 mb-2">
            <Icon name="purchase" size={14} />
            <b className="text-[12.5px]">{`أصناف الفاتورة ${row.number}`}</b>
            <span className="text-[11.5px] text-ink-3">
              {`${row.supplierName} — ${row.warehouseName}`}
            </span>
            <div className="flex-1" />
            <button type="button" className="btn btn-ghost no-print" onClick={onClose}>
              <Icon name="x" size={13} /> إغلاق
            </button>
          </div>
          {row.items?.length ? (
            <table style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>المنتج</th>
                  <th>الرمز</th>
                  <th>الكمية</th>
                  <th>كلفة الوحدة</th>
                  <th>الخصم</th>
                  <th>الإجمالي</th>
                </tr>
              </thead>
              <tbody>
                {row.items.map((it, i) => (
                  <tr key={`${row.id}-${it.sku || i}`}>
                    <td>{it.name}</td>
                    <td style={{ fontFamily: 'monospace', fontSize: 11.5 }}>{it.sku || '—'}</td>
                    <td>{formatNumber(it.qty)}</td>
                    <td>{formatIQD(it.cost)}</td>
                    <td>{it.discount ? formatIQD(it.discount) : '—'}</td>
                    <td style={{ fontWeight: 700 }}>{formatIQD(it.total)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ fontWeight: 800 }}>
                  <td colSpan={2}>{`${row.itemsCount} صنف`}</td>
                  <td>{formatNumber(row.itemsQty)}</td>
                  <td colSpan={2} />
                  <td>{formatIQD(row.total)}</td>
                </tr>
              </tfoot>
            </table>
          ) : (
            <div className="text-ink-3 text-[12.5px]">لا أصناف مسجّلة على هذه الفاتورة</div>
          )}
        </div>
      </td>
    </tr>
  );
}

export function PurchasesReport({ range }) {
  const user = useAuth((s) => s.user);
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [sort, setSort] = useState('value');
  const [openInvoice, setOpenInvoice] = useState('');

  const topTableRef = useRef(null);
  const cancelledTableRef = useRef(null);

  const top = usePurchasesTopProducts({
    ...range, supplierId, warehouseId, sort, limit: 50
  });
  const cancelled = useCancelledPurchases({
    ...range, supplierId, warehouseId, limit: 200
  });

  const topRows = top.data?.rows || [];
  const topTotals = top.data?.totals || {
    products: 0, qty: 0, value: 0, grossValue: 0, discount: 0, invoices: 0, suppliers: 0
  };
  const cancelledRows = cancelled.data?.rows || [];
  const cancelledTotals = cancelled.data?.totals || {
    count: 0, value: 0, paid: 0, items: 0
  };

  // قوائم الترشيح: اتحاد ما ظهر في التقريرين حتى لا يختفي مورد له فواتير
  // ملغاة فقط (أو مرحّلة فقط) من القائمة.
  const facets = useMemo(() => {
    const sup = new Map();
    const wh = new Map();
    [top.data?.facets, cancelled.data?.facets].forEach((f) => {
      (f?.suppliers || []).forEach((s) => {
        if (s.supplierId) sup.set(String(s.supplierId), s.name);
      });
      (f?.warehouses || []).forEach((w) => {
        if (w.warehouseId) wh.set(String(w.warehouseId), w.name);
      });
    });
    return {
      suppliers: [...sup.entries()].map(([id, name]) => ({ id, name })),
      warehouses: [...wh.entries()].map(([id, name]) => ({ id, name }))
    };
  }, [top.data, cancelled.data]);

  const period = periodLabelOf(range);
  const supplierName = facets.suppliers.find((s) => s.id === supplierId)?.name || '';
  const warehouseName = facets.warehouses.find((w) => w.id === warehouseId)?.name || '';

  const activeFilters = [
    supplierId ? `المورد: ${supplierName || supplierId}` : null,
    warehouseId ? `المخزن: ${warehouseName || warehouseId}` : null,
    `الفرز: ${sort === 'qty' ? 'الكمية المشتراة' : 'قيمة المشتريات'}`
  ].filter(Boolean);

  const exportMeta = [`الفترة: ${period}`, ...activeFilters];

  function resetFilters() {
    setSupplierId('');
    setWarehouseId('');
    setSort('value');
  }

  const maxBar = topRows.reduce((m, r) => Math.max(m, sort === 'qty' ? r.qty : r.value), 0) || 1;

  const topColumns = [
    { key: 'name', label: 'المنتج', value: (r) => r.name },
    { key: 'sku', label: 'الرمز', value: (r) => r.sku || '' },
    { key: 'qty', label: 'الكمية المشتراة', value: (r) => Number(r.qty || 0) },
    { key: 'value', label: 'إجمالي قيمة المشتريات', value: (r) => Number(r.value || 0) },
    { key: 'purchasesCount', label: 'عدد مرات الشراء', value: (r) => Number(r.purchasesCount || 0) },
    { key: 'avgCost', label: 'متوسط الكلفة', value: (r) => Number(r.avgCost || 0) },
    { key: 'discount', label: 'خصم الأصناف', value: (r) => Number(r.discount || 0) },
    { key: 'supplier', label: 'المورد', value: (r) => r.topSupplierName || '—' },
    { key: 'suppliers', label: 'كل المورّدين', value: (r) => (r.supplierNames || []).join(' · ') },
    { key: 'warehouse', label: 'المخزن', value: (r) => (r.warehouseNames || []).join(' · ') },
    { key: 'lastPurchaseAt', label: 'آخر شراء', value: (r) => dateAr(r.lastPurchaseAt) },
    { key: 'lastInvoice', label: 'آخر فاتورة', value: (r) => r.lastInvoiceNumber || '' },
    { key: 'share', label: 'الحصة %', value: (r) => `${r.share ?? 0}%` }
  ];

  const cancelledColumns = [
    { key: 'number', label: 'رقم الفاتورة', value: (r) => r.number },
    { key: 'supplier', label: 'المورد', value: (r) => r.supplierName },
    { key: 'warehouse', label: 'المخزن', value: (r) => r.warehouseName },
    { key: 'date', label: 'تاريخ الفاتورة', value: (r) => dateAr(r.date) },
    { key: 'total', label: 'قيمة الفاتورة', value: (r) => Number(r.total || 0) },
    { key: 'paid', label: 'المدفوع قبل الإلغاء', value: (r) => Number(r.paid || 0) },
    { key: 'items', label: 'عدد الأصناف', value: (r) => Number(r.itemsCount || 0) },
    { key: 'qty', label: 'إجمالي الكميات', value: (r) => Number(r.itemsQty || 0) },
    { key: 'cancelledAt', label: 'تاريخ الإلغاء', value: (r) => (r.cancelledAt ? formatDateTime(r.cancelledAt) : '—') },
    { key: 'cancelledBy', label: 'من ألغاها', value: (r) => r.cancelledByName },
    { key: 'reason', label: 'سبب الإلغاء', value: (r) => r.cancelReason || '—' }
  ];

  return (
    <div className="space-y-3" dir="rtl">
      {/* شريط الأدوات والترشيح — لا يُطبع */}
      <div className="no-print card p-3 flex flex-wrap items-center gap-2">
        <select
          className="input"
          style={{ maxWidth: 220 }}
          value={supplierId}
          onChange={(e) => setSupplierId(e.target.value)}
          aria-label="المورد"
          data-testid="purchases-supplier"
        >
          <option value="">كل المورّدين</option>
          {facets.suppliers.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>

        <select
          className="input"
          style={{ maxWidth: 220 }}
          value={warehouseId}
          onChange={(e) => setWarehouseId(e.target.value)}
          aria-label="المخزن"
          data-testid="purchases-warehouse"
        >
          <option value="">كل المخازن</option>
          {facets.warehouses.map((w) => (
            <option key={w.id} value={w.id}>{w.name}</option>
          ))}
        </select>

        <select
          className="input"
          style={{ maxWidth: 200 }}
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          aria-label="الفرز"
          data-testid="purchases-sort"
        >
          <option value="value">الأعلى قيمة شراء</option>
          <option value="qty">الأعلى كمية مشتراة</option>
        </select>

        {supplierId || warehouseId || sort !== 'value' ? (
          <button type="button" className="btn btn-ghost" onClick={resetFilters}>
            <Icon name="x" size={14} /> مسح الترشيح
          </button>
        ) : null}

        <div className="flex-1" />

        <button type="button" className="btn btn-primary" onClick={() => window.print()}>
          <Icon name="print" size={14} /> طباعة (A4)
        </button>
      </div>

      {top.data == null && !top.isPending ? (
        <div className="rounded-[10px] border border-[#FDE68A] bg-[#FFFBEB] text-[#92400E] text-[12.5px] font-semibold px-3.5 py-2.5">
          تعذّر تحميل تقارير المشتريات — تحقّق من الاتصال أو الصلاحيات ثم أعد المحاولة.
        </div>
      ) : null}

      <div className="print-root">
        {/* شعار المركز من الإعدادات — أعلى الورقة ووسطها */}
        <PrintLogo />
        <section
          className="print-section"
          style={{
            background: 'linear-gradient(135deg,#FF7A1A,#FFB627)',
            color: '#fff',
            borderRadius: 10,
            padding: 18,
            marginBottom: 14
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <img src="/kidzo-mark.png" alt="" width={44} height={44} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 800 }}>تقارير المشتريات</div>
              <div style={{ fontSize: 12, opacity: 0.9 }}>
                أكثر المنتجات شراءً وفواتير المشتريات الملغاة — المسوّدات لا تُحتسب
              </div>
            </div>
            <div style={{ textAlign: 'end', fontSize: 11 }}>
              <div>الفترة: <b>{period}</b></div>
              <div>فواتير مرحّلة: <b>{formatNumber(topTotals.invoices)}</b></div>
              {user ? <div>المُصدِر: {user.fullName || user.username}</div> : null}
            </div>
          </div>
        </section>

        {supplierId || warehouseId ? (
          <div
            className="print-section"
            style={{
              background: '#FFFBEB',
              border: '1px solid #FDE68A',
              color: '#92400E',
              borderRadius: 10,
              padding: '8px 12px',
              marginBottom: 12,
              fontSize: 12,
              fontWeight: 600
            }}
          >
            ترشيح مُطبَّق — {activeFilters.join(' · ')}
          </div>
        ) : null}

        <section className="print-section" style={{ marginBottom: 14 }}>
          <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(4, minmax(0,1fr))' }}>
            <KpiCard
              label="إجمالي قيمة المشتريات"
              value={formatIQD(topTotals.value)}
              sub={`${formatNumber(topTotals.invoices)} فاتورة مرحّلة`}
              accent="#C2570D"
            />
            <KpiCard
              label="إجمالي الكميات المشتراة"
              value={formatNumber(topTotals.qty)}
              sub={`${formatNumber(topTotals.products)} صنف`}
            />
            <KpiCard
              label="خصومات الأصناف"
              value={formatIQD(topTotals.discount)}
              sub="مطروحة من قيمة الشراء"
              accent="#15803D"
            />
            <KpiCard
              label="فواتير ملغاة"
              value={formatNumber(cancelledTotals.count)}
              sub={`بقيمة ${formatIQD(cancelledTotals.value)} — خارج الإجماليات`}
              accent="#B91C1C"
            />
          </div>
        </section>

        {/* أكثر المنتجات شراءً */}
        <section className="print-section" style={{ marginBottom: 16 }}>
          <div
            style={{
              marginBottom: 10,
              paddingBottom: 8,
              borderBottom: '2px solid #FF7A1A',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>أكثر المنتجات شراءً</h3>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                الكمية وقيمة الشراء وعدد مرات الشراء والمورّد — من الفواتير المرحّلة فقط
              </div>
            </div>
            <ExportButton
              rows={topRows}
              columns={topColumns}
              filename="purchases-top-products"
              title="تقرير أكثر المنتجات شراءً"
              subtitle="الفواتير المرحّلة فقط — المسوّدات والملغاة مستثناة"
              meta={exportMeta}
              totalsRow={[
                'الإجمالي',
                '',
                topTotals.qty,
                topTotals.value,
                topTotals.invoices,
                '',
                topTotals.discount,
                '', '', '', '', '', '100%'
              ]}
              tableRef={topTableRef}
            />
          </div>

          {top.isPending ? (
            <div className="text-ink-3 text-center py-10 text-sm">جاري تحميل تقرير المشتريات...</div>
          ) : topRows.length === 0 ? (
            <div className="text-ink-3 text-center py-10 text-sm">
              لا مشتريات مرحّلة في هذه الفترة
            </div>
          ) : (
            <div ref={topTableRef} style={{ overflowX: 'auto' }}>
              <table dir="rtl" data-testid="purchases-top-table">
                <thead>
                  <tr>
                    <th>المنتج</th>
                    <th>الكمية المشتراة</th>
                    <th>إجمالي قيمة المشتريات</th>
                    <th>عدد مرات الشراء</th>
                    <th>متوسط الكلفة</th>
                    <th>المورد</th>
                    <th>المخزن</th>
                    <th>آخر شراء</th>
                    <th>الحصة</th>
                  </tr>
                </thead>
                <tbody>
                  {topRows.map((r) => (
                    <tr key={String(r.productId)}>
                      <td>
                        <div style={{ fontWeight: 700 }}>{r.name}</div>
                        {r.sku ? (
                          <div style={{ fontSize: 11, color: '#9ca3af', fontFamily: 'monospace' }}>
                            {r.sku}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ fontWeight: 700 }}>{formatNumber(r.qty)}</td>
                      <td style={{ fontWeight: 800, color: '#C2570D' }}>{formatIQD(r.value)}</td>
                      <td>{formatNumber(r.purchasesCount)}</td>
                      <td>{formatIQD(r.avgCost)}</td>
                      <td>
                        {r.topSupplierName}
                        {(r.supplierNames || []).length > 1 ? (
                          <div style={{ fontSize: 10.5, color: '#9ca3af' }}>
                            {`+${r.supplierNames.length - 1} مورد آخر`}
                          </div>
                        ) : null}
                      </td>
                      <td style={{ fontSize: 11.5 }}>{(r.warehouseNames || []).join(' · ')}</td>
                      <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>{dateAr(r.lastPurchaseAt)}</td>
                      <td style={{ minWidth: 110 }}>
                        <div className="h-1.5 bg-paper-2 rounded-full relative print-hide-on-paper">
                          <div
                            className="absolute inset-y-0 right-0 bg-orange rounded-full"
                            style={{
                              width: `${Math.round(((sort === 'qty' ? r.qty : r.value) / maxBar) * 100)}%`
                            }}
                          />
                        </div>
                        <div style={{ fontSize: 11, color: '#6b7280' }}>{`${r.share ?? 0}%`}</div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 800, background: '#FFF7ED' }}>
                    <td>{`الإجمالي (${formatNumber(topTotals.products)} صنف)`}</td>
                    <td>{formatNumber(topTotals.qty)}</td>
                    <td style={{ color: '#C2570D' }}>{formatIQD(topTotals.value)}</td>
                    <td>{formatNumber(topTotals.invoices)}</td>
                    <td colSpan={5} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </section>

        {/* فواتير المشتريات الملغاة */}
        <section className="print-section">
          <div
            style={{
              marginBottom: 10,
              paddingBottom: 8,
              borderBottom: '2px solid #B91C1C',
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800 }}>فواتير المشتريات الملغاة</h3>
              <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
                الفاتورة الملغاة لا تُحذف — تُعرض بمن ألغاها وسببه وتفاصيل أصنافها، وخارج كل إجمالي
              </div>
            </div>
            <ExportButton
              rows={cancelledRows}
              columns={cancelledColumns}
              filename="purchases-cancelled"
              title="تقرير فواتير المشتريات الملغاة"
              subtitle="قائمة توضيحية — لا تدخل قيمة المشتريات ولا كلفة المخزون"
              meta={[
                `الفترة: ${period}`,
                ...activeFilters.slice(0, 2),
                `العدد: ${cancelledTotals.count}`,
                `القيمة: ${formatIQD(cancelledTotals.value)}`
              ]}
              totalsRow={[
                'الإجمالي',
                '', '', '',
                cancelledTotals.value,
                cancelledTotals.paid,
                cancelledTotals.items,
                '', '', '', ''
              ]}
              tableRef={cancelledTableRef}
            />
          </div>

          {cancelled.isPending ? (
            <div className="text-ink-3 text-center py-10 text-sm">جاري تحميل الفواتير الملغاة...</div>
          ) : cancelledRows.length === 0 ? (
            <div className="text-ink-3 text-center py-10 text-sm">
              لا فواتير مشتريات ملغاة في هذه الفترة
            </div>
          ) : (
            <div ref={cancelledTableRef} style={{ overflowX: 'auto' }}>
              <table dir="rtl" data-testid="purchases-cancelled-table">
                <thead>
                  <tr>
                    <th>رقم الفاتورة</th>
                    <th>المورد</th>
                    <th>التاريخ</th>
                    <th>قيمة الفاتورة</th>
                    <th>من ألغاها</th>
                    <th>تاريخ الإلغاء</th>
                    <th>سبب الإلغاء</th>
                    <th className="no-print">الأصناف</th>
                  </tr>
                </thead>
                <tbody>
                  {cancelledRows.map((r) => (
                    [
                      <tr key={r.id}>
                        <td style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.number}</td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{r.supplierName}</div>
                          <div style={{ fontSize: 10.5, color: '#9ca3af' }}>{r.warehouseName}</div>
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>{dateAr(r.date)}</td>
                        <td style={{ fontWeight: 800 }}>
                          {formatIQD(r.total)}
                          {r.paid > 0 ? (
                            <div style={{ fontSize: 10.5, color: '#9ca3af', fontWeight: 600 }}>
                              {`مدفوع قبل الإلغاء ${formatIQD(r.paid)}`}
                            </div>
                          ) : null}
                        </td>
                        <td style={{ fontWeight: 600 }}>{r.cancelledByName}</td>
                        <td style={{ whiteSpace: 'nowrap', fontSize: 11.5 }}>
                          {r.cancelledAt ? formatDateTime(r.cancelledAt) : '—'}
                        </td>
                        <td>
                          <Pill variant="danger">{r.cancelReason || 'بلا سبب مسجَّل'}</Pill>
                        </td>
                        <td className="no-print">
                          <button
                            type="button"
                            className="btn"
                            style={{ padding: '3px 9px', fontSize: 12 }}
                            onClick={() => setOpenInvoice(openInvoice === r.id ? '' : r.id)}
                          >
                            {`${r.itemsCount} صنف`}
                            <Icon name="chevronD" size={12} />
                          </button>
                        </td>
                      </tr>,
                      openInvoice === r.id ? (
                        <CancelledInvoiceDetails
                          key={`${r.id}-details`}
                          row={r}
                          onClose={() => setOpenInvoice('')}
                        />
                      ) : null
                    ]
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ fontWeight: 800, background: '#FEF2F2' }}>
                    <td colSpan={3}>{`الإجمالي (${formatNumber(cancelledTotals.count)} فاتورة)`}</td>
                    <td style={{ color: '#B91C1C' }}>{formatIQD(cancelledTotals.value)}</td>
                    <td colSpan={4} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 8 }}>
            {`إجمالي ما دُفع على الفواتير الملغاة: ${formatIQD(cancelledTotals.paid)} — يبقى رصيداً `}
            لدى المورد حتى يُسترد أو يُخصم من فاتورة لاحقة.
          </div>
        </section>
      </div>
    </div>
  );
}
